import {
  formatMakeupsAnnualPeriodLabel,
  formatMakeupsAnnualPeriodRangeLabel,
  formatMakeupsAnnualPeriodShortLabel,
  type MakeupsAnnualPeriod,
} from "./makeupAnnualPeriod";

export const MAKEUPS_ANNUAL_REQUEST_LIMIT_KEY = "MAKEUPS_ANNUAL_REQUEST_LIMIT";

/** Stored value meaning no cap on makeup requests per student per year. */
export const MAKEUPS_NO_ANNUAL_LIMIT = 99_999;

export const DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT = MAKEUPS_NO_ANNUAL_LIMIT;

const MIN_MAKEUPS_ANNUAL_REQUEST_LIMIT = 1;
const MAX_MAKEUPS_ANNUAL_REQUEST_LIMIT = MAKEUPS_NO_ANNUAL_LIMIT;

export function isValidDraftMakeupsAnnualRequestLimit(raw: string): boolean {
  const trimmed = String(raw).trim();
  if (trimmed === "") return false;

  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed)) return false;

  return (
    parsed >= MIN_MAKEUPS_ANNUAL_REQUEST_LIMIT &&
    parsed < MAX_MAKEUPS_ANNUAL_REQUEST_LIMIT
  );
}

export function isMakeupsNoAnnualLimit(limit: number): boolean {
  return limit >= MAKEUPS_NO_ANNUAL_LIMIT;
}

export function parseMakeupsAnnualRequestLimit(
  raw: string | number | null | undefined,
): number {
  if (raw == null) return DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT;

  const parsed =
    typeof raw === "number" ? raw : Number.parseInt(String(raw).trim(), 10);

  if (!Number.isFinite(parsed)) return DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT;
  if (parsed < MIN_MAKEUPS_ANNUAL_REQUEST_LIMIT) {
    return MIN_MAKEUPS_ANNUAL_REQUEST_LIMIT;
  }
  if (parsed > MAX_MAKEUPS_ANNUAL_REQUEST_LIMIT) {
    return MAX_MAKEUPS_ANNUAL_REQUEST_LIMIT;
  }
  return parsed;
}

export function formatMakeupsAnnualLimitPhrase(
  limit: number,
  period: MakeupsAnnualPeriod,
): string {
  if (isMakeupsNoAnnualLimit(limit)) {
    return "no annual limit";
  }
  return `${limit} per student per ${formatMakeupsAnnualPeriodShortLabel(period).toLowerCase()} year`;
}

export function formatMakeupsAnnualLimitShortPhrase(
  limit: number,
  period: MakeupsAnnualPeriod,
): string {
  if (isMakeupsNoAnnualLimit(limit)) {
    return "no annual cap";
  }
  return `${limit}/${formatMakeupsAnnualPeriodShortLabel(period)} per student`;
}

export function formatMakeupsAnnualLimitHowItWorksLine(
  limit: number,
  period: MakeupsAnnualPeriod,
): string {
  if (isMakeupsNoAnnualLimit(limit)) {
    return `There is no limit on how many makeup requests each student can submit per ${formatMakeupsAnnualPeriodLabel(period).toLowerCase()} year.`;
  }
  return `Each student can submit up to ${limit} makeup request${limit === 1 ? "" : "s"} per ${formatMakeupsAnnualPeriodLabel(period).toLowerCase()} year. Rejected or cancelled requests do not count toward this limit.`;
}

export function formatMakeupsAnnualLimitEligiblePanelLine(
  limit: number,
  period: MakeupsAnnualPeriod,
): string {
  if (isMakeupsNoAnnualLimit(limit)) {
    return "";
  }
  return `Students may submit up to ${limit} request${limit === 1 ? "" : "s"} per ${formatMakeupsAnnualPeriodLabel(period).toLowerCase()} year.`;
}

export function formatMakeupsAnnualRequestsRemainingLabel(
  used: number,
  limit: number,
  period: MakeupsAnnualPeriod,
  referenceDate: Date = new Date(),
): string | null {
  if (isMakeupsNoAnnualLimit(limit)) return null;
  const remaining = Math.max(0, limit - used);
  const periodLabel = formatMakeupsAnnualPeriodRangeLabel(period, referenceDate);

  if (remaining === 0) {
    return `Annual limit reached for ${periodLabel} (${used}/${limit})`;
  }
  return `${remaining} of ${limit} annual request${limit === 1 ? "" : "s"} left in ${periodLabel}`;
}

export function formatMakeupsAnnualLimitAdminSummary(
  expiryDays: number,
  annualLimit: number,
  period: MakeupsAnnualPeriod,
  formatExpiryPhrase: (days: number) => string,
  isNoExpiry: (days: number) => boolean,
): string {
  const expiryPart = isNoExpiry(expiryDays)
    ? "no expiration"
    : formatExpiryPhrase(expiryDays);
  const annualPart = isMakeupsNoAnnualLimit(annualLimit)
    ? "no annual cap"
    : formatMakeupsAnnualLimitShortPhrase(annualLimit, period);
  return `${expiryPart} · ${annualPart}`;
}
